import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Search, 
  Filter, 
  Edit, 
  Trash2, 
  Eye, 
  Plus, 
  RotateCcw, 
  Package, 
  Building,
  Zap,
  Settings,
  X,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';
import { adminPanelAPI, solarAPI } from '../../services/api';
import { SolarPanel } from '../../types';
import { Alert } from '../common/Alert';
import { Button } from '../common/Button';
import { Input } from '../common/Input';
import { Link } from 'react-router-dom';
import { EditPanelModal } from './EditPanelModal';

interface PanelFilters {
  search?: string;
  CellType?: string;
  minWatts?: number;
  maxWatts?: number;
  minEfficiency?: number;
  maxEfficiency?: number;
}

export const ManagePanels: React.FC = () => {
  const [panels, setPanels] = useState<SolarPanel[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [selectedPanel, setSelectedPanel] = useState<SolarPanel | null>(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [cellTypes, setCellTypes] = useState<string[]>([]);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [limit] = useState(20);

  // Filters
  const [filters, setFilters] = useState<PanelFilters>({});

  useEffect(() => {
    loadPanels();
    loadCellTypes();
  }, [currentPage, filters]);

  const loadPanels = async () => {
    setLoading(true);
    try {
      const result = await adminPanelAPI.getAllPanels(currentPage, limit, filters);
      setPanels(result.panels);
      setTotalPages(result.totalPages);
      setTotalCount(result.totalCount);
    } catch (err: any) {
      setError('Failed to load panels');
    } finally {
      setLoading(false);
    }
  };

  const loadCellTypes = async () => {
    try {
      const types = await solarAPI.getCellTypes();
      setCellTypes(types);
    } catch (err: any) {
      console.error('Failed to load cell types:', err);
    }
  };

  const handleDeletePanel = async (panel: SolarPanel) => {
    if (!window.confirm(`Are you sure you want to delete panel ${panel.SKU}?`)) {
      return;
    }

    try {
      await adminPanelAPI.deletePanel(panel.id!);
      setSuccess('Panel deleted successfully');
      loadPanels();
    } catch (err: any) {
      setError('Failed to delete panel');
    }
  };

  const handleRestorePanel = async (panel: SolarPanel) => {
    try {
      await adminPanelAPI.restorePanel(panel.id!);
      setSuccess('Panel restored successfully');
      loadPanels();
    } catch (err: any) {
      setError('Failed to restore panel');
    }
  };

  const handleEditSuccess = (updatedPanel: SolarPanel) => {
    setSuccess('Panel updated successfully');
    loadPanels();
    setSelectedPanel(null);
  };

  const handleFilterChange = (key: keyof PanelFilters, value: any) => {
    setFilters(prev => ({
      ...prev,
      [key]: value === '' ? undefined : value
    }));
    setCurrentPage(1);
  };

  const clearFilters = () => {
    setFilters({});
    setCurrentPage(1);
  };

  const hasActiveFilters = Object.values(filters).some(value => value !== undefined && value !== '');

  const renderPanelCard = (panel: SolarPanel) => (
    <motion.div
      key={panel.id}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow"
    >
      <div className="flex justify-between items-start mb-4">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">{panel.SKU}</h3>
          <p className="text-gray-600 text-sm mt-1">{panel.Description}</p>
        </div>
        <div className="flex space-x-2">
          <Button
            size="sm"
            variant="secondary"
            onClick={() => {
              setSelectedPanel(panel);
              setShowDetailsModal(true);
            }}
          >
            <Eye className="h-3 w-3" />
          </Button>
          <Button
            size="sm"
            variant="secondary"
            onClick={() => {
              setSelectedPanel(panel);
              setShowEditModal(true);
            }}
          >
            <Edit className="h-3 w-3" />
          </Button>
          <Button
            size="sm"
            variant="danger"
            onClick={() => handleDeletePanel(panel)}
          >
            <Trash2 className="h-3 w-3" />
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
        <div>
          <span className="font-medium text-gray-500">Power:</span>
          <div className="text-gray-900">{panel.Watts_W}W</div>
        </div>
        <div>
          <span className="font-medium text-gray-500">Efficiency:</span>
          <div className="text-gray-900">{panel.ModuleEfficiency_percent}%</div>
        </div>
        <div>
          <span className="font-medium text-gray-500">Cell Type:</span>
          <div className="text-gray-900">{panel.CellType}</div>
        </div>
        <div>
          <span className="font-medium text-gray-500">Cells:</span>
          <div className="text-gray-900">{panel.NumberOfCells_count}</div>
        </div>
      </div>

      {panel.inventory && panel.inventory.length > 0 && (
        <div className="mt-4 pt-4 border-t border-gray-200">
          <div className="flex items-center mb-2">
            <Package className="h-4 w-4 text-green-600 mr-2" />
            <span className="text-sm font-medium text-gray-900">
              Available Inventory ({panel.inventory.length} suppliers)
            </span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {panel.inventory.slice(0, 2).map((inv) => (
              <div key={inv.id} className="bg-gray-50 rounded p-2 text-xs">
                <div className="font-medium">{inv.supplier?.companyName}</div>
                <div className="text-gray-600">
                  Qty: {inv.quantity} | ${inv.pricePerUnit}/unit
                </div>
              </div>
            ))}
          </div>
          {panel.inventory.length > 2 && (
            <div className="text-xs text-gray-500 mt-1">
              +{panel.inventory.length - 2} more suppliers
            </div>
          )}
        </div>
      )}
    </motion.div>
  );

  const renderDetailsModal = () => {
    if (!selectedPanel || !showDetailsModal) return null;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto"
        >
          <div className="p-6 border-b border-gray-200">
            <div className="flex justify-between items-center">
              <h2 className="text-2xl font-bold text-gray-900">{selectedPanel.SKU}</h2>
              <Button
                variant="secondary"
                onClick={() => setShowDetailsModal(false)}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
            <p className="text-gray-600 mt-1">{selectedPanel.Description}</p>
          </div>

          <div className="p-6 space-y-6">
            {/* Basic Information */}
            <div className="bg-blue-50 rounded-lg p-4">
              <div className="flex items-center mb-3">
                <Package className="h-5 w-5 text-blue-600 mr-2" />
                <h3 className="font-semibold text-blue-900">Basic Information</h3>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 text-sm">
                <div><span className="font-medium">SKU:</span> {selectedPanel.SKU}</div>
                <div><span className="font-medium">Cell Type:</span> {selectedPanel.CellType}</div>
                <div><span className="font-medium">Connector:</span> {selectedPanel.ConnectorType}</div>
                <div><span className="font-medium">Cells:</span> {selectedPanel.NumberOfCells_count}</div>
              </div>
            </div>

            {/* Power Specifications */}
            <div className="bg-yellow-50 rounded-lg p-4">
              <div className="flex items-center mb-3">
                <Zap className="h-5 w-5 text-yellow-600 mr-2" />
                <h3 className="font-semibold text-yellow-900">Power Specifications</h3>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 text-sm">
                <div><span className="font-medium">Power:</span> {selectedPanel.Watts_W}W</div>
                <div><span className="font-medium">Pmax:</span> {selectedPanel.Pmax_W}W</div>
                <div><span className="font-medium">Vmp:</span> {selectedPanel.Vmp_V}V</div>
                <div><span className="font-medium">Imp:</span> {selectedPanel.Imp_A}A</div>
                <div><span className="font-medium">Voc:</span> {selectedPanel.Voc_V}V</div>
                <div><span className="font-medium">Isc:</span> {selectedPanel.Isc_A}A</div>
                <div><span className="font-medium">Efficiency:</span> {selectedPanel.ModuleEfficiency_percent}%</div>
              </div>
            </div>

            {/* Physical Specifications */}
            <div className="bg-purple-50 rounded-lg p-4">
              <div className="flex items-center mb-3">
                <Settings className="h-5 w-5 text-purple-600 mr-2" />
                <h3 className="font-semibold text-purple-900">Physical Specifications</h3>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 text-sm">
                <div><span className="font-medium">Length:</span> {selectedPanel.Length_mm}mm</div>
                <div><span className="font-medium">Width:</span> {selectedPanel.Width_mm}mm</div>
                <div><span className="font-medium">Thickness:</span> {selectedPanel.Thickness_mm}mm</div>
                <div><span className="font-medium">Weight:</span> {selectedPanel.Weight_kg}kg</div>
                <div><span className="font-medium">Cable Length:</span> {selectedPanel.CableLength_mm}mm</div>
              </div>
            </div>

            {/* Inventory Information */}
            {selectedPanel.inventory && selectedPanel.inventory.length > 0 && (
              <div className="bg-green-50 rounded-lg p-4">
                <div className="flex items-center mb-3">
                  <Building className="h-5 w-5 text-green-600 mr-2" />
                  <h3 className="font-semibold text-green-900">Inventory ({selectedPanel.inventory.length} suppliers)</h3>
                </div>
                <div className="space-y-3">
                  {selectedPanel.inventory.map((inv) => (
                    <div key={inv.id} className="bg-white rounded-lg p-3 border border-green-200">
                      <div className="flex justify-between items-start">
                        <div>
                          <div className="font-medium text-gray-900">{inv.supplier?.companyName}</div>
                          <div className="text-sm text-gray-600">
                            {inv.supplier?.city}, {inv.supplier?.state}, {inv.supplier?.country}
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="font-medium">Qty: {inv.quantity}</div>
                          <div className="text-sm text-gray-600">${inv.pricePerUnit}/unit</div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </motion.div>
      </div>
    );
  };

  return (
    <div className="max-w-7xl mx-auto">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-8"
      >
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Manage Solar Panels</h1>
            <p className="text-gray-600 mt-2">
              {totalCount} panels total • Page {currentPage} of {totalPages}
            </p>
          </div>
          <Link to="/admin/create">
            <Button>
              <Plus className="h-4 w-4 mr-2" />
              Add New Panel
            </Button>
          </Link>
        </div>
      </motion.div>

      {error && <Alert type="error" message={error} onClose={() => setError('')} />}
      {success && <Alert type="success" message={success} onClose={() => setSuccess('')} />}

      {/* Search and Filter Controls */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-lg shadow-lg p-6 mb-8"
      >
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-4">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Search panels by SKU, description, or cell type..."
                value={filters.search || ''}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                className="pl-10"
              />
            </div>
          </div>
          <div className="flex items-center space-x-2">
            {hasActiveFilters && (
              <Button
                variant="secondary"
                onClick={clearFilters}
                className="flex items-center"
              >
                <X className="h-4 w-4 mr-1" />
                Clear Filters
              </Button>
            )}
            <Button
              onClick={() => setShowFilters(!showFilters)}
              className={`flex items-center ${
                showFilters ? 'bg-blue-50 border-blue-300 text-blue-700' : ''
              }`}
              variant={showFilters ? 'secondary' : 'secondary'}
            >
              <Filter className="h-4 w-4 mr-1" />
              Filters
            </Button>
          </div>
        </div>

        {/* Advanced Filters */}
        {showFilters && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="border-t border-gray-200 pt-4"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Cell Type
                </label>
                <select
                  value={filters.CellType || ''}
                  onChange={(e) => handleFilterChange('CellType', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#63B56F]"
                >
                  <option value="">All Cell Types</option>
                  {cellTypes.map((type) => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Min Power (W)
                </label>
                <Input
                  type="number"
                  value={filters.minWatts || ''}
                  onChange={(e) => handleFilterChange('minWatts', e.target.value ? parseInt(e.target.value) : undefined)}
                  placeholder="Min watts"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Max Power (W)
                </label>
                <Input
                  type="number"
                  value={filters.maxWatts || ''}
                  onChange={(e) => handleFilterChange('maxWatts', e.target.value ? parseInt(e.target.value) : undefined)}
                  placeholder="Max watts"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Min Efficiency (%)
                </label>
                <Input
                  type="number"
                  step="0.1"
                  value={filters.minEfficiency || ''}
                  onChange={(e) => handleFilterChange('minEfficiency', e.target.value ? parseFloat(e.target.value) : undefined)}
                  placeholder="Min efficiency"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Max Efficiency (%)
                </label>
                <Input
                  type="number"
                  step="0.1"
                  value={filters.maxEfficiency || ''}
                  onChange={(e) => handleFilterChange('maxEfficiency', e.target.value ? parseFloat(e.target.value) : undefined)}
                  placeholder="Max efficiency"
                />
              </div>
            </div>
          </motion.div>
        )}
      </motion.div>

      {/* Panels Grid */}
      {loading ? (
        <div className="flex justify-center items-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#63B56F]"></div>
        </div>
      ) : panels.length === 0 ? (
        <div className="bg-white rounded-lg shadow-lg p-12 text-center">
          <Package className="h-16 w-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No panels found</h3>
          <p className="text-gray-600 mb-6">
            {hasActiveFilters ? 'Try adjusting your filters' : 'Create your first solar panel to get started'}
          </p>
          {hasActiveFilters ? (
            <Button onClick={clearFilters} variant="secondary">
              <RotateCcw className="h-4 w-4 mr-2" />
              Clear Filters
            </Button>
          ) : (
            <Link to="/admin/create">
              <Button>
                <Plus className="h-4 w-4 mr-2" />
                Add New Panel
              </Button>
            </Link>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {panels.map(renderPanelCard)}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="mt-8 flex justify-center items-center space-x-4">
          <Button
            variant="secondary"
            onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
            disabled={currentPage === 1}
          >
            <ChevronLeft className="h-4 w-4" />
            Previous
          </Button>
          
          <div className="flex items-center space-x-2">
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              const page = Math.max(1, currentPage - 2) + i;
              if (page > totalPages) return null;
              
              return (
                <button
                  key={page}
                  onClick={() => setCurrentPage(page)}
                  className={`px-3 py-1 rounded-md text-sm ${
                    currentPage === page
                      ? 'bg-[#63B56F] text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {page}
                </button>
              );
            })}
          </div>

          <Button
            variant="secondary"
            onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
            disabled={currentPage === totalPages}
          >
            Next
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      )}

      {/* Modals */}
      {renderDetailsModal()}
      
      {/* Edit Panel Modal */}
      {selectedPanel && (
        <EditPanelModal
          panel={selectedPanel}
          isOpen={showEditModal}
          onClose={() => {
            setShowEditModal(false);
            setSelectedPanel(null);
          }}
          onSuccess={handleEditSuccess}
        />
      )}
    </div>
  );
};
